import { ProjectRunAnalysisQuestion, RubricCore } from '../../../../Types/dealGPT';

type AreaMapType = {
  [area: string]: {
    [dimension: string]: ProjectRunAnalysisQuestion[];
  };
};

export const tileColors = {
  1: '#F52C00',
  2: '#FF8A00',
  3: '#FFC700',
  4: '#00FCB0',
  5: '#1DD566',
};

export const getUniqueAreas = (questions: ProjectRunAnalysisQuestion[]): string[] => {
  // Step 1: Create a map to store each area with its minimum display order
  const areaDisplayOrderMap: { [area: string]: number } = {};

  questions.forEach((question) => {
    const area = question.area as string;
    const displayOrder = question.displayOrder ?? Number.MAX_SAFE_INTEGER;

    // Update the minimum display order for the area
    if (!(area in areaDisplayOrderMap) || displayOrder < areaDisplayOrderMap[area]) {
      areaDisplayOrderMap[area] = displayOrder;
    }
  });

  // Step 2: Sort areas based on their minimum display order
  const sortedAreas = Object.keys(areaDisplayOrderMap).sort(
    (areaA, areaB) => areaDisplayOrderMap[areaA] - areaDisplayOrderMap[areaB]
  );

  return sortedAreas;
};

export const calculateDimensionValues = (
  questions: ProjectRunAnalysisQuestion[],
  areaRubricCore?: Record<string, RubricCore[]>
): { [dimension: string]: number } => {
  const dimensionValuesObj: { [dimension: string]: number } = {};

  questions.forEach((question) => {
    const dimension = question.dimension || '';
    const dimensionRubrics = areaRubricCore?.[dimension];
    const rubric = dimensionRubrics?.find(
      (r) =>
        r.maturityLevelDisplayName === question.value ||
        r.maturityLevel === parseInt(question.value || '0')
    );
    dimensionValuesObj[dimension] = rubric?.maturityLevel ?? 0;
  });

  return dimensionValuesObj;
};

export const calculateOverallRating = (dimensionValues: {
  [dimension: string]: number;
}): number => {
  const values = Object.values(dimensionValues);
  const total = values.reduce((acc, cur) => acc + cur, 0);
  return values.length === 0 ? 0 : total / values.length;
};

export const getTileColor = (index: number, rating: number, maxRating: number): string => {
  const scale = maxRating / 5;
  if (Math.round(rating) >= scale * 5) return tileColors['5'];
  if (Math.round(rating) >= scale * 4 && index <= scale * 4) return tileColors['4'];
  if (Math.round(rating) >= scale * 3 && index <= scale * 3) return tileColors['3'];
  if (Math.round(rating) >= scale * 2 && index <= scale * 2) return tileColors['2'];
  if (Math.round(rating) > 0 && index <= scale) return tileColors['1'];
  return '#E8ECEF';
};

export const groupByAreaAndDimension = (questions: ProjectRunAnalysisQuestion[]): AreaMapType => {
  const areaMap: AreaMapType = {};

  questions.forEach((question) => {
    const area = question.area ?? 'undefined';
    if (!areaMap[area]) {
      areaMap[area] = {};
    }

    const dimension = question.dimension ?? 'undefined';
    if (!areaMap[area][dimension]) {
      areaMap[area][dimension] = [];
    }

    areaMap[area][dimension].push(question);
  });

  // Sort each dimension array in descending order by createdAt
  Object.keys(areaMap).forEach((area) => {
    Object.keys(areaMap[area]).forEach((dimension) => {
      areaMap[area][dimension].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    });
  });

  return areaMap;
};
